/**
 * BrainFilesStore — web mirror of `Sources/Store/BrainFilesStore.swift` (LIVE path).
 *
 * The real files client. Talks to the Brain Worker's OWNER-ONLY files REST API
 * (docs/BRAIN_API.md), which dispatches to the operator's Mac bridge. Because the
 * work runs on the Mac via the bridge, this REQUIRES the engine online and can
 * long-poll for up to ~25s or time out with 504.
 *
 * AUTH: every request carries `Authorization: Bearer brain_*` — the SAME durable
 * token BrainAuth holds in localStorage (NOT the short-lived Supabase JWT).
 *
 * Status codes (docs/BRAIN_API.md): 200 ok; 401 not signed in; 403 caller not
 * owner (owner-only); 502 errored on the Mac; 503 couldn't enqueue; 504 engine
 * offline/slow. Non-2xx surfaces as BrainFsError(status) so the view can render
 * the right owner-only vs engine-offline copy.
 */

import type { BrainConfig } from '../config';
import { BrainFsError, decodeFilesList, type FilesListResponse } from '../models/brainFs';
import type { FilesStore } from './FilesStore';

export class BrainFilesStore implements FilesStore {
  private readonly baseURL: string;
  private readonly brainToken: () => Promise<string | null>;

  constructor(config: BrainConfig, brainToken: () => Promise<string | null>) {
    this.baseURL = config.brainBaseURL;
    this.brainToken = brainToken;
  }

  async list(path?: string): Promise<FilesListResponse> {
    const token = await this.brainToken();
    if (!token) throw new BrainFsError('No Brain token', 401);

    const url = new URL('/files', this.baseURL);
    if (path) url.searchParams.set('path', path);

    let res: Response;
    try {
      res = await fetch(url.toString(), {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
    } catch (e) {
      // A transport failure means we never reached the engine — treat as offline
      // (504-equivalent) so the view shows the engine-offline state.
      throw new BrainFsError(e instanceof Error ? e.message : 'Network error', 504);
    }

    const text = await res.text();
    if (!res.ok) {
      throw new BrainFsError(`Brain returned HTTP ${res.status}`, res.status);
    }
    try {
      return decodeFilesList(JSON.parse(text));
    } catch {
      throw new BrainFsError('Unexpected Brain response: not JSON');
    }
  }
}
